/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { BatchTestResultItem } from 'src/luis/types/stores/testPane/BatchTestResultItem';
import { BatchTestState, BatchTestStore } from 'src/luis/types/stores/testPane/BatchTestStore';

/**
 * An batch test store factory object.
 *
 * @param batchTest The batch test data.
 */
export const createBatchTestStore = (batchTest: Partial<BatchTestStore>): BatchTestStore => {
    const store: BatchTestStore = {
        id: batchTest.id,
        state: 'notRunning',
        name: batchTest.name,
        resultItems: batchTest.resultItems ? batchTest.resultItems : [],
        lastRunDate: batchTest.lastRunDate,
        lastResult: batchTest.lastResult,
        size: batchTest.size,
        errorMessage: batchTest.errorMessage,

        /**
         * Renames the batch test with the given name.
         *
         * @param name The name to rename the batch test with.
         */
        rename(name: string): void {
            this.name = name;
        },

        /**
         * Setter for state.
         *
         * @param state The new state.
         */
        setState(state: BatchTestState) {
            this.state = state;
        },

        /**
         * Setter for lastRunDate.
         *
         * @param date The new date.
         */
        setLastRunDate(date: Date) {
            this.lastRunDate = date;
        },

        /**
         * Setter for lastResult.
         *
         * @param result The new result.
         */
        setLastResult(result: number) {
            this.lastResult = result;
        },

        /**
         * Setter for resultItems.
         *
         * @param resultItems The items resulting from running
         * the batch test dataset.
         */
        setResultItems(resultItems: BatchTestResultItem[]) {
            this.resultItems = resultItems;
        },

        /**
         * Setter for error message if exists.
         *
         * @param message the error message to be set.
         */
        setErrorMessage(message: string) {
            this.errorMessage = message;
        }
    };

    return observable<BatchTestStore>(
        store,
        {
            name: observable,
            state: observable,
            lastResult: observable,
            lastRunDate: observable,
            errorMessage: observable,
            rename: action,
            setState: action,
            setLastRunDate: action,
            setLastResult: action,
            setResultItems: action,
            setErrorMessage: action
        },
        { deep: false }
    );
};
